/**
 * PMV 2.0 Wave 5.5 — Bulk Daily Log Sheets (Field Ops) API client.
 * Sheet = workflow envelope; pmv_daily_logs remains operational authority.
 */
import api from '@/services/api.service';

function unwrap<T>(res: unknown): T {
  const body = (res as { data?: unknown })?.data ?? res;
  return (body as { data?: T })?.data ?? (body as T);
}

export type PmvDailyLogSheetStatus =
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'VERIFIED'
  | 'RETURNED'
  | 'CLOSED'
  | 'CANCELLED';

export interface PmvDailyLogSheet {
  id: string;
  sheet_number: string;
  project_id: string;
  site_reference?: string | null;
  work_front_id?: string | null;
  operating_date: string;
  shift_code: string;
  status: PmvDailyLogSheetStatus | string;
  row_version: number;
  entry_count: number;
  notes?: string | null;
  submitted_at?: string | null;
  verified_at?: string | null;
  return_reason?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface PmvDailyLogSheetEntry {
  id: string;
  sheet_id: string;
  line_no: number;
  pmv_profile_id?: string | null;
  allocation_id?: string | null;
  equipment_code?: string | null;
  daily_log_id?: string | null;
  expected_flag: boolean;
  unexpected_reason?: string | null;
  shift_code?: string | null;
  sequence_no: number;
  operator_employee_id?: string | null;
  work_front_id?: string | null;
  working_minutes: number;
  idle_minutes: number;
  standby_minutes: number;
  breakdown_minutes: number;
  other_non_operating_minutes: number;
  opening_meter_value?: number | null;
  closing_meter_value?: number | null;
  work_description?: string | null;
  remarks?: string | null;
  row_payload_json?: Record<string, unknown> | null;
  last_row_result?: string | null;
  last_row_message?: string | null;
}

export interface PmvDailyLogSheetDetail extends PmvDailyLogSheet {
  entries?: PmvDailyLogSheetEntry[];
}

export interface PmvSheetBulkSaveResult {
  sheet_id: string;
  row_version: number;
  results: Array<{
    entry_id: string | null;
    line_no: number;
    result: string;
    message?: string;
    daily_log_id?: string | null;
  }>;
}

export interface PmvSheetValidateResult {
  sheet_id: string;
  status: string;
  entry_count: number;
  issues: Array<{
    entry_id?: string;
    line_no?: number;
    code: string;
    message: string;
    severity?: string;
  }>;
  ok: boolean;
}

const pmvFieldOpsService = {
  async getMeta() {
    const res = await api.get('/pmv/field-ops/meta');
    return unwrap<Record<string, unknown>>(res);
  },

  async listSheets(params?: {
    page?: number;
    pageSize?: number;
    project_id?: string;
    operating_date?: string;
    status?: string;
  }) {
    const res = await api.get('/pmv/field-ops/sheets', { params });
    const body = (res as { data?: unknown })?.data ?? res;
    const nested = body as {
      data?: PmvDailyLogSheet[];
      pagination?: { page: number; pageSize: number; total: number };
    };
    if (nested?.data && nested.pagination) return nested;
    const unwrapped = unwrap<{
      data: PmvDailyLogSheet[];
      pagination: { page: number; pageSize: number; total: number };
    }>(res);
    return unwrapped;
  },

  async createSheet(body: {
    project_id: string;
    operating_date: string;
    shift_code?: string;
    site_reference?: string | null;
    work_front_id?: string | null;
    notes?: string | null;
    idempotency_key?: string | null;
    load_expected?: boolean;
  }) {
    const res = await api.post('/pmv/field-ops/sheets', body);
    return unwrap<PmvDailyLogSheetDetail>(res);
  },

  async getSheet(id: string) {
    const res = await api.get(`/pmv/field-ops/sheets/${id}`);
    return unwrap<PmvDailyLogSheetDetail>(res);
  },

  async loadExpected(id: string) {
    const res = await api.post(`/pmv/field-ops/sheets/${id}/load-expected`);
    return unwrap<PmvDailyLogSheetDetail>(res);
  },

  async bulkSave(id: string, body: { row_version: number; rows: Record<string, unknown>[] }) {
    const res = await api.post(`/pmv/field-ops/sheets/${id}/bulk-save`, body);
    return unwrap<PmvSheetBulkSaveResult>(res);
  },

  async validateSheet(id: string) {
    const res = await api.post(`/pmv/field-ops/sheets/${id}/validate`);
    return unwrap<PmvSheetValidateResult>(res);
  },

  async submitSheet(id: string, body: { row_version: number; idempotency_key?: string }) {
    const res = await api.post(`/pmv/field-ops/sheets/${id}/submit`, body);
    return unwrap<PmvDailyLogSheetDetail>(res);
  },

  async verifySheet(id: string, body: { row_version: number }) {
    const res = await api.post(`/pmv/field-ops/sheets/${id}/verify`, body);
    return unwrap<PmvDailyLogSheetDetail>(res);
  },

  async returnSheet(id: string, body: { row_version: number; reason?: string }) {
    const res = await api.post(`/pmv/field-ops/sheets/${id}/return`, body);
    return unwrap<PmvDailyLogSheetDetail>(res);
  },

  async closeSheet(id: string, body: { row_version: number }) {
    const res = await api.post(`/pmv/field-ops/sheets/${id}/close`, body);
    return unwrap<PmvDailyLogSheetDetail>(res);
  },

  async previewImport(body: {
    sheet_id: string;
    rows: Record<string, unknown>[];
    row_version?: number;
  }) {
    const res = await api.post('/pmv/field-ops/import/preview', body);
    return unwrap<{
      sheet_id: string;
      preview: Array<Record<string, unknown>>;
      errors?: Array<{ row: number; message: string }>;
    }>(res);
  },

  async applyImport(body: {
    sheet_id: string;
    rows: Record<string, unknown>[];
    row_version: number;
  }) {
    const res = await api.post('/pmv/field-ops/import/apply', body);
    return unwrap<PmvSheetBulkSaveResult & { sheet?: PmvDailyLogSheetDetail }>(res);
  },
};

export default pmvFieldOpsService;
